import React from 'react';
import {View} from 'react-native';
import {RFPercentage, RFValue} from 'react-native-responsive-fontsize';
import {differenceInDays} from 'date-fns';
import {useTheme} from 'styled-components/native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import {VoucherDTO} from '../../../../dtos/VoucherDTO';
import {
  ActionBtn,
  ActionBtnIcon,
  ActionBtnLeft,
  ActionBtnText,
  BadgesRow,
  BorderRing,
  BottomSection,
  CardContainer,
  CardOuter,
  CardTouchable,
  CategoryBadge,
  CategoryBadgeText,
  CityText,
  DashedLine,
  DividerRow,
  ExpiredOverlayBackground,
  ExpiredOverlayContent,
  ExpiredOverlayTitle,
  FuelCircle,
  FuelCircleImage,
  FuelCircleLetter,
  FuelInfo,
  FuelNameText,
  FuelRow,
  InfoDivider,
  InfoItem,
  InfoLabelSmall,
  InfoRow,
  InfoTextStack,
  InfoValue,
  NotchCircle,
  RefundBtn,
  RefundBtnText,
  TopSection,
  getCategoryTokens,
  getFuelCircleColor,
} from './styles';

interface ICouponCard {
  data: VoucherDTO;
  action?: () => void;
  isBlocked?: boolean;
  onSupportPress?: () => void;
  onRefundPress?: () => void;
  cardWidth?: number;
}

function getCategoryLabel(categoria: string): string | null {
  if (categoria?.toLowerCase() === 'ouro') {
    return 'SUPER BARATO';
  }
  if (categoria?.toLowerCase() === 'prata') {
    return 'BARATO';
  }
  return null;
}

export function CouponCard({
  data,
  action,
  isBlocked,
  onSupportPress,
  onRefundPress,
}: ICouponCard) {
  const theme = useTheme();
  const diasVencimento = differenceInDays(
    new Date(data.dt_validade),
    new Date(),
  );
  const categoryLabel = getCategoryLabel(data.categoria);
  const tokens = getCategoryTokens(data.categoria, theme);
  const isUrgent = diasVencimento <= 2 && data.valido;
  const isExpiringToday = diasVencimento <= 0 && data.valido;
  const fuelCircleBg = getFuelCircleColor(data.name);

  const fuelImageUri =
    data.name === 'Gasolina Comum'
      ? 'https://barataocombustiveis.com.br/assets/gasolina-comum-cba8f39fc373e83253072449ee2ef3984b2f08f311125ba26c7a8f9434951d47.png'
      : data.fuelImage;

  const handlePress = () => {
    if (!isBlocked && data.valido) {
      action?.();
    }
  };

  return (
    <CardOuter>
      <CardContainer
        colors={
          data.categoria === 'ouro'
            ? theme.colors.coupon_gold_gradient
            : theme.colors.coupon_silver_gradient
        }
        locations={[0, 0.32, 0.72, 1]}
        start={{x: 0.05, y: 0}}
        end={{x: 1, y: 1}}
        expired={!data.valido}>
        {/* z=1: borda colorida, renderizada antes do conteúdo */}
        <BorderRing
          categoria={data.categoria}
          expiringToday={isExpiringToday}
        />

        {/* z=2: conteúdo do card */}
        <CardTouchable activeOpacity={0.88} onPress={handlePress}>
          {/* TOP */}
          <TopSection>
            <BadgesRow>
              {categoryLabel ? (
                <CategoryBadge categoria={data.categoria} theme={theme}>
                  <CategoryBadgeText categoria={data.categoria}>
                    {categoryLabel}
                  </CategoryBadgeText>
                </CategoryBadge>
              ) : (
                <View />
              )}
              <MaterialCommunityIcons
                name={data.tipo === 'C' ? 'gas-station' : 'store'}
                size={RFValue(25)}
                color={tokens.iconColor}
              />
            </BadgesRow>
          </TopSection>

          {/* DIVIDER — onLayout mede a posição para os NotchCircles */}
          <DividerRow>
            <DashedLine categoria={data.categoria} theme={theme} />
          </DividerRow>

          {/* BOTTOM */}
          <BottomSection>
            <FuelRow>
              <FuelCircle bg={fuelCircleBg}>
                {fuelImageUri ? (
                  <FuelCircleImage
                    source={{uri: fuelImageUri}}
                    resizeMode="contain"
                  />
                ) : (
                  <FuelCircleLetter numberOfLines={1} ellipsizeMode="tail">
                    {data.name[0].toUpperCase()}
                  </FuelCircleLetter>
                )}
              </FuelCircle>
              <FuelInfo>
                <FuelNameText numberOfLines={1} ellipsizeMode="tail">
                  {data.name.toUpperCase()}
                </FuelNameText>
                {!!data.cidade && (
                  <CityText numberOfLines={1} ellipsizeMode="tail">
                    {data.cidade}
                  </CityText>
                )}
              </FuelInfo>
            </FuelRow>
            <InfoRow>
              {data.tipo === 'C' && (
                <InfoItem>
                  <MaterialCommunityIcons
                    name="water"
                    size={RFValue(23)}
                    color={tokens.iconColor}
                  />
                  <InfoTextStack>
                    <InfoValue>{data.liters}</InfoValue>
                    <InfoLabelSmall>LITROS</InfoLabelSmall>
                  </InfoTextStack>
                </InfoItem>
              )}

              {data.tipo === 'C' && data.valido && (
                <InfoDivider categoria={data.categoria} />
              )}

              {data.valido && (
                <InfoItem>
                  <MaterialCommunityIcons
                    name="calendar-outline"
                    size={RFValue(23)}
                    color={tokens.iconColor}
                  />
                  <InfoTextStack>
                    <InfoValue urgent={isUrgent}>{diasVencimento}</InfoValue>
                    <InfoLabelSmall urgent={isExpiringToday}>
                      {' '}
                      {diasVencimento === 1 ? 'DIA' : 'DIAS'}
                    </InfoLabelSmall>
                  </InfoTextStack>
                </InfoItem>
              )}
            </InfoRow>

            {!isBlocked && data.valido && (
              <>
                <ActionBtn
                  activeOpacity={0.85}
                  onPress={handlePress}
                  categoria={data.categoria}>
                  <ActionBtnLeft>
                    <ActionBtnIcon
                      name="qrcode"
                      size={RFValue(18)}
                      categoria={data.categoria}
                      color="#fff"
                    />
                    <ActionBtnText categoria={data.categoria}>
                      Ver QR Code
                    </ActionBtnText>
                  </ActionBtnLeft>
                  <ActionBtnIcon
                    name="chevron-right"
                    size={RFValue(18)}
                    categoria={data.categoria}
                    color="#fff"
                  />
                </ActionBtn>
              </>
            )}
          </BottomSection>
        </CardTouchable>

        {/* z=3: NotchCircles como filhos diretos do CardContainer (overflow:hidden).
          Clipados em x=0 (borda externa) → cobrem a BorderRing. */}
        <NotchCircle
          side="left"
          categoria={data.categoria}
          style={{top: RFPercentage(8)}}
        />
        <NotchCircle
          side="right"
          categoria={data.categoria}
          style={{top: RFPercentage(8)}}
        />
      </CardContainer>

      {isBlocked && (
        <>
          <ExpiredOverlayBackground />
          <ExpiredOverlayContent>
            <MaterialCommunityIcons
              name="lock"
              size={RFValue(32)}
              color="rgba(255,255,255,1)"
            />
            <ExpiredOverlayTitle>
              Cupom bloqueado. Fale com o suporte para desbloquear.
            </ExpiredOverlayTitle>
            <RefundBtn activeOpacity={0.75} onPress={() => onSupportPress}>
              <RefundBtnText>Falar com suporte</RefundBtnText>
            </RefundBtn>
          </ExpiredOverlayContent>
        </>
      )}

      {!data.valido && !isBlocked && (
        <>
          <ExpiredOverlayBackground />
          <ExpiredOverlayContent>
            <MaterialCommunityIcons
              name="clock-remove-outline"
              size={RFValue(32)}
              color="rgba(255,255,255,1)"
            />
            <ExpiredOverlayTitle>Cupom expirado</ExpiredOverlayTitle>
            <RefundBtn activeOpacity={0.75} onPress={onRefundPress}>
              <RefundBtnText>Solicitar Reembolso</RefundBtnText>
            </RefundBtn>
          </ExpiredOverlayContent>
        </>
      )}
    </CardOuter>
  );
}
